import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

// Default location (Delhi)
const DEFAULT_LOCATION = {
  name: 'Delhi',
  coords: { lat: 28.6139, lng: 77.2090 },
  type: 'default'
};

// Popular locations for quick selection
export const POPULAR_LOCATIONS = [
  { id: 'delhi', name: 'Delhi', coords: { lat: 28.6139, lng: 77.2090 } },
  { id: 'gurgaon', name: 'Gurgaon', coords: { lat: 28.4595, lng: 77.0266 } },
  { id: 'manesar', name: 'Manesar', coords: { lat: 28.4089, lng: 76.9366 } },
  { id: 'murthal', name: 'Murthal', coords: { lat: 29.0234, lng: 77.0577 } },
  { id: 'jaipur', name: 'Jaipur', coords: { lat: 26.9124, lng: 75.7873 } },
  { id: 'sonipat', name: 'Sonipat', coords: { lat: 28.9288, lng: 77.0913 } },
  { id: 'dharuhera', name: 'Dharuhera', coords: { lat: 28.2057, lng: 76.7962 } },
  { id: 'neemrana', name: 'Neemrana', coords: { lat: 27.9855, lng: 76.3844 } },
];

const LocationContext = createContext(null);

// Standalone reverse geocode function (no hooks dependency)
async function fetchLocationName(lat, lng) {
  // Strategy 1: OpenStreetMap Nominatim (free, reliable, no key needed)
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=16&addressdetails=1`,
      { headers: { 'Accept-Language': 'en' } }
    );
    if (res.ok) {
      const data = await res.json();
      if (data.address) {
        const addr = data.address;
        const area = addr.suburb || addr.neighbourhood || addr.village || addr.hamlet || addr.town || '';
        const city = addr.city || addr.state_district || addr.county || '';
        if (area && city) return `${area}, ${city}`;
        if (city) return city;
        if (area) return area;
        if (addr.state) return addr.state;
      }
    }
  } catch (err) {
    console.warn('[Location] Nominatim failed:', err.message); // eslint-disable-line no-console
  }

  // Strategy 2: Google Maps JS Geocoder (if loaded)
  if (window.google?.maps?.Geocoder) {
    try {
      const name = await new Promise((resolve) => {
        const geocoder = new window.google.maps.Geocoder();
        const timeoutId = setTimeout(() => resolve(null), 5000); // 5s timeout
        geocoder.geocode({ location: { lat, lng } }, (results, status) => {
          clearTimeout(timeoutId);
          if (status === 'OK' && results?.length > 0) {
            const comps = results[0].address_components;
            let area = '', city = '';
            for (const c of comps) {
              if (c.types.includes('sublocality_level_1') || c.types.includes('sublocality')) area = c.long_name;
              if (c.types.includes('locality')) city = c.long_name;
              if (c.types.includes('route') && !area) area = c.long_name;
            }
            if (area && city) resolve(`${area}, ${city}`);
            else if (city) resolve(city);
            else if (area) resolve(area);
            else resolve(null);
          } else {
            resolve(null);
          }
        });
      });
      if (name) return name;
    } catch (err) {
      console.warn('[Location] Google JS geocoder failed:', err.message); // eslint-disable-line no-console
    }
  }

  return null;
}

export function LocationProvider({ children }) {
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [isLoadingCurrent, setIsLoadingCurrent] = useState(true);
  const [locationError, setLocationError] = useState(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  // When we get GPS coords, resolve the name
  const handleGPSSuccess = useCallback(async (position) => {
    const lat = position.coords.latitude;
    const lng = position.coords.longitude;
    const coords = { lat, lng };

    // Immediately show coords are available
    const initialLocation = { name: 'Current Location', coords, type: 'current' };
    setCurrentLocation(initialLocation);
    setSelectedLocation((prev) => prev || initialLocation);
    setIsLoadingCurrent(false);
    setLocationError(null);

    // Now resolve the actual name (async, updates when ready)
    try {
      const locationName = await fetchLocationName(lat, lng);
      if (isMountedRef.current && locationName) {
        const resolved = { name: locationName, coords, type: 'current' };
        setCurrentLocation(resolved);
        setSelectedLocation((prev) => {
          if (prev?.type === 'current') return resolved;
          return prev;
        });
      }
    } catch (err) {
      // Geocode error handled silently
    }
  }, []);

  const requestGPS = useCallback(() => {
    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by your browser');
      setIsLoadingCurrent(false);
      setSelectedLocation((prev) => prev || DEFAULT_LOCATION);
      return;
    }

    setIsLoadingCurrent(true);
    setLocationError(null);

    // Try high accuracy first, then fall back to low accuracy
    navigator.geolocation.getCurrentPosition(
      handleGPSSuccess,
      () => {
        // High accuracy failed → try low accuracy
        navigator.geolocation.getCurrentPosition(
          handleGPSSuccess,
          (err) => {
            let msg = 'Unable to detect location.';
            if (err.code === 1) msg = 'Location permission denied. Please enable it in browser settings.';
            else if (err.code === 2) msg = 'Location unavailable. Check GPS/network settings.';
            else if (err.code === 3) msg = 'Location timed out. Tap to retry.';

            setLocationError(msg);
            setIsLoadingCurrent(false);
            setSelectedLocation((prev) => prev || DEFAULT_LOCATION);
          },
          { enableHighAccuracy: false, timeout: 15000, maximumAge: 300000 }
        );
      },
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 }
    );
  }, [handleGPSSuccess]);

  // Request on mount
  useEffect(() => { requestGPS(); }, [requestGPS]);

  const retryGPS = useCallback(() => { requestGPS(); }, [requestGPS]);

  const selectLocation = (location) => { setSelectedLocation(location); };

  const setCurrentLocationAsSelected = () => {
    if (currentLocation) {
      setSelectedLocation(currentLocation);
    } else {
      retryGPS();
    }
  };

  const selectPopularLocation = (locationId) => {
    const location = POPULAR_LOCATIONS.find(l => l.id === locationId);
    if (location) {
      setSelectedLocation({ name: location.name, coords: location.coords, type: 'selected' });
    }
  };

  const activeLocation = selectedLocation || DEFAULT_LOCATION;

  const value = {
    selectedLocation,
    currentLocation,
    activeLocation,
    isLoadingCurrent,
    locationError,
    hasCurrentLocation: !!currentLocation,
    selectLocation,
    setCurrentLocationAsSelected,
    selectPopularLocation,
    retryGPS,
    isCurrentLocation: selectedLocation?.type === 'current',
    isDefaultLocation: !selectedLocation || selectedLocation?.type === 'default',
    locationName: activeLocation.name,
    coords: activeLocation.coords,
  };

  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  );
}

export function useLocation() {
  const context = useContext(LocationContext);
  if (!context) {
    throw new Error('useLocation must be used within a LocationProvider');
  }
  return context;
}

export default LocationContext;
